<template>
  <div>
    <div class="hide-for-print">
      <!-- header -->
      <div v-if="!returnDetailStore.error" class="margin-bottom-l">
        <BreadcrumbWrapper current-page="rdv" />
        <Notification
          v-if="returnDetails?.isRmaReturn && !isShippingDetailsLoading && isShippingDetailsError"
          severity="error"
          class="margin-top-xs"
          :closable="false"
        >
          {{ rmaShippingDetailsError }}
        </Notification>
        <h1 class="margin-top-xs">Return Details</h1>
      </div>
      <!-- content -->
      <div v-for="(notification, index) in notifications" :key="index" class="margin-bottom-xs" :class="notification.class">
        <Notification :severity="notification?.severity" :closable="false">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-html="notification.text"></div>
        </Notification>
      </div>
      <div v-if="showTrackReturnLink" class="margin-bottom-xs">
        <a id="track-return-link" class="link-lg" href="#" @click.prevent="showTrackReturnSlideout()"> Track Return </a>
      </div>
      <p v-if="returnDetails.returnStatusCode !== 303" class="large" v-text="returnDetails.returnSubStatusDesc"></p>
      <div>
        <ProgressBarComponent
          v-if="returnDropOffMethod && returnDetails.rmaType !== 'REFUND_RETURN_NOT_REQUIRED'"
          :progress-bar-data="progressBarData"
          :progress-bar-value="progressBarValue?.statusBarValue"
        />
        <div
          v-if="returnStatusCode === 300"
          class="grid-x return-instructions"
          :class="{ 'padding-bottom-s': returnDropOffMethod === RETURN_METHOD_KEY_MAP.posReturn }"
        >
          <div
            v-if="returnDropOffMethod !== RETURN_METHOD_KEY_MAP.posReturn"
            class="cell small-12 medium-8 margin-top-s medium-margin-top-l padding-bottom-s"
          >
            <div
              v-if="hazmatItems.length && (returnDropOffMethod === RETURN_METHOD_KEY_MAP.ups || isFedExReturnMethod)"
              class="medium-margin-right-s"
            >
              <HazmatReturnInstructions :items="hazmatItems" :is-mkp-order="isMarketplaceOrder" :is-fedex-return-method="isFedExReturnMethod" />
            </div>
            <div v-else>
              <div class="h5 margin-bottom-xs" v-text="returnInstructions.uopTitle"></div>
              <ol class="margin-bottom-xs">
                <li v-for="(instruction, k) in instructions ?? []" :key="k" class="margin-left-xs" v-text="instruction"></li>
              </ol>
            </div>
            <p v-if="showBankRefundText" class="margin-bottom-xs">Depending on your bank, it may take a few days for your refund to appear</p>
            <a id="locatorLink" class="link-body" href="#" @click.prevent="triggerLocatorSlideout()" v-text="locatorLinkText"></a>
          </div>
          <div class="cell small-12 medium-4">
            <div v-if="returnDropOffMethod === RETURN_METHOD_KEY_MAP.ups || isFedExReturnMethod" class="medium-margin-top-l padding-bottom-s">
              <button id="get-return-label" class="primary expanded button" @click.prevent="showReturnLabel()">Get Return Label</button>
            </div>
            <div v-if="showQrCode" class="margin-top-s qr-code margin-bottom-s">
              <QRCode
                :qrcode-number="getQRcodeNumber()"
                :qrcode-url="returnDetails.dropOffUrl"
                :scan-text="getScanText()"
                :hide-number="hideQRCodeNumber"
                is-text-left
              />
            </div>
          </div>
        </div>
        <div v-if="showTrackReturnCTA" class="grid-x">
          <div class="cell small-12 medium-8 large small-margin-top-m">
            <p v-if="isMarketplaceOrder" v-text="trackReturnSellerInstruction"></p>
          </div>
          <div class="cell small-12 medium-4 small-margin-top-m">
            <button id="track-return-cta" class="primary expanded button" @click.prevent.stop="showTrackReturnSlideout()">Track Return</button>
          </div>
        </div>
      </div>
      <div :class="returnDetails.returnStatusCode === 300 ? 'full-line margin-top-xs' : ''"></div>
      <div class="margin-top-l margin-bottom-md">
        <p
          v-if="returnDetails.returnStatusCode === 303"
          :class="isCompletedOrder && returnDetails.rmaType !== 'REFUND_RETURN_NOT_REQUIRED' ? 'cell small-12 large' : 'h6'"
          v-text="returnDetails.returnSubStatusDesc"
        ></p>
        <p v-if="isCompletedOrder" class="margin-bottom-xs">Depending on your bank, it may take a few days for your refund to appear</p>
        <p v-if="returnDetails.rmaType === 'REFUND_RETURN_NOT_REQUIRED'" data-testid="no-return-required-message" class="margin-top-xs">
          <strong>There’s no need to return your items.</strong>
          You can keep, donate, or recycle them.
        </p>
        <div v-if="showRefundInfo" class="padding-top-xxs padding-bottom-xs refund-info">
          <a id="refundLink" class="link-body" href="#" @click.prevent="showRefundSlideout()">When will I get my refund?</a>
        </div>
        <ProductThumbnailWrapper :products="generateProducts" :return-status="returnStatusCode" show-policy-message />
      </div>
      <ReturnSummary
        v-if="returnDropOffMethod"
        :return-details="returnDetails"
        :replace-and-exchange-option="returnDetailStore.SSRE_RDV"
        page="return-details"
      />
      <NeedHelp v-if="isMcom" class="padding-top-xs text-center align-center" />
    </div>
    <!-- print label -->
    <div v-if="returnDropOffMethod === RETURN_METHOD_KEY_MAP.ups || isFedExReturnMethod" class="print-return-label-confirmation-container">
      <PrintReturnLabel page="return-details" :return-details="enhancedReturnDetails" />
    </div>
  </div>
</template>

<script setup>
import { defineAsyncComponent, markRaw, onMounted, computed } from 'vue';
import { fireTag } from '@vue-util/tagManager';
import Notification from '@nuxt-common/atomic-ui/app/components/notification/Notification.vue';
import { sanitize } from '@vue-util/sanitize';
import { UserStatus } from '@vue-util/cookie';
import { getShipmentTrackingLabel, getShipmentStatus } from '@/utils/ShipmentStatus';
import transformProducts from '@/utils/returns/TransformProducts';
import ProgressBarComponent from '@/components/ProgressBar';
import NeedHelp from '@/components/common/NeedHelp';
import HazmatReturnInstructions from '@/components/returns/HazmatReturnInstructions';
import BreadcrumbWrapper from '@/components/common/BreadcrumbWrapper';
import ProductThumbnailWrapper from '@/components/common/ProductThumbnailWrapper';
import QRCode from '@/components/common/QRCode.vue';
import ReturnSummary from '@/components/returns/ReturnSummary';
import PrintReturnLabel from '@/components/returns/PrintReturnLabel';
import { getTitle } from '@/utils/returns/ReturnInstruction';
import {
  RETURN_METHOD_KEY_MAP,
  REFUND_METHOD,
  LOCATOR_LINK_TEXT,
  returnInstructions,
  trackReturnSellerInstruction,
  RMA_SHIPPING_DETAILS_ERROR,
} from 'config';
import { RETURN_EXPIRY_WINDOW, NOTIFICATION_TYPES, LOCATOR_LOCATION_RADIUS, LOCATOR_RESULT_LIMIT } from '#shared/constants';

const route = useRoute();
const { isMcom, isBcom } = useBrand();
const returnDetailStore = useReturnDetailStore();
const returnItemStore = useReturnItemStore();
const trackPackageStore = useTrackPackageStore();
const { registerSlideout, toggleSlideout } = useSlideout();
const { registerModal, toggleModal } = useModal();
const envPropsStore = useEnvPropsStore();

const returnDetails = computed(() => returnDetailStore.returnDetails);

const isShippingDetailsLoading = computed(() => returnDetailStore.getShippingDetails);
const isShippingDetailsError = computed(() => returnItemStore.errors.getShippingDetails);

const enhancedReturnDetails = computed(() => {
  const shippingLabels = returnItemStore.shippingDetails.shippingLabels ?? [];
  const shippingLabel = shippingLabels[0] ?? {};

  return {
    ...returnDetails.value,
    ...(returnDetails.value.pidToken || returnDetails.value.isRmaReturn
      ? {
          labelImageUrl: shippingLabel.base64Label,
          rmaId: returnDetails.value.pendingReturnId,
        }
      : {}),
  };
});
const isRMAVisibilityChangesEnabled = computed(() => returnDetails.value.rmaVisibilityChangesEnabled);
const isMarketplaceOrder = computed(() => returnDetails.value.isMkpOrder);
const rmaShippingDetailsError = computed(() => RMA_SHIPPING_DETAILS_ERROR);
const returnSubmittedDate = computed(() => returnDetails.value.returnSubmittedDate);
const returnDropOffMethod = computed(() => returnDetails.value.returnDropOffMethod);
const isFedExReturnMethod = computed(() => isBcom && returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.fedex);
const returnStatusCode = computed(() => {
  const statusCode = returnDetails.value.returnStatusCode;
  if (statusCode === 307) return 300;
  return statusCode;
});
const isCompletedOrder = computed(() => isRMAVisibilityChangesEnabled.value && returnStatusCode.value === 303);
const showAlertMessage = computed(
  () =>
    returnDetails.value.orderReturnAlertMessage?.web?.message_type &&
    returnDetails.value.orderReturnAlertMessage?.web?.message &&
    NOTIFICATION_TYPES.includes(returnDetails.value.orderReturnAlertMessage?.web?.message_type?.toLowerCase()),
);
const sanitizedAlertMessage = computed(() => sanitize(returnDetails.value.orderReturnAlertMessage?.web?.message));
const notifications = computed(() => {
  const allNotifications = [];
  if (showAlertMessage.value) {
    allNotifications.push({
      text: sanitizedAlertMessage.value,
      severity: returnDetails.value.orderReturnAlertMessage?.web?.message_type?.toLowerCase(),
      class: 'medium-12',
    });
  }
  if (returnDetailStore.returnLabel?.showNotification) {
    allNotifications.push({
      text: `Your return label has been sent to ${returnDetailStore.returnLabel?.emailAddress}`,
      severity: 'success',
      class: 'medium-10',
    });
  }
  return allNotifications;
});
const showTrackReturn = computed(() => {
  if (returnDetails.value.rmaVisibilityChangesEnabled || !isMarketplaceOrder.value) {
    return returnStatusCode.value === 301 || returnStatusCode.value === 302;
  }
  // if KS off and it's 3P
  return returnStatusCode.value === 300;
});
const returnWindowExpired = computed(() => {
  const daysFromReturnSubmission = Math.ceil(Math.abs(new Date() - new Date(returnSubmittedDate.value)) / (1000 * 60 * 60 * 24));
  return daysFromReturnSubmission > RETURN_EXPIRY_WINDOW;
});
const showTrackReturnLink = computed(() => {
  const isReturnWindowOpen = !returnWindowExpired.value;
  const isValidMarketplaceOrder = isMarketplaceOrder.value && returnStatusCode.value === 300;
  const isPosReturnWithRMAEnabled = returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.posReturn && isRMAVisibilityChangesEnabled.value;

  if (isReturnWindowOpen && isValidMarketplaceOrder) {
    return !isPosReturnWithRMAEnabled;
  }

  return false;
});

const showTrackReturnCTA = computed(() => {
  if ((!showTrackReturn.value && returnDropOffMethod.value !== RETURN_METHOD_KEY_MAP.posReturn) || returnWindowExpired.value) {
    return false;
  }
  if (!isMarketplaceOrder.value || (isMarketplaceOrder.value && isRMAVisibilityChangesEnabled.value && returnStatusCode.value !== 303)) {
    return true;
  }
  return false;
});
const progressBarData = computed(() => {
  switch (returnDropOffMethod.value) {
    case RETURN_METHOD_KEY_MAP.labelFree:
    case RETURN_METHOD_KEY_MAP.ups:
      if (returnStatusCode.value === 'COMPLETE') {
        return getShipmentTrackingLabel('UPS_COMPLETED_RETURN');
      }
      if (!returnDetails.value.rmaVisibilityChangesEnabled && isMarketplaceOrder.value) {
        return getShipmentTrackingLabel('MARKETPLACE_RETURN');
      }
      return getShipmentTrackingLabel('UPS_RETURN');
    case RETURN_METHOD_KEY_MAP.storeReturn:
      return getShipmentTrackingLabel('STORE_RETURN');
    case RETURN_METHOD_KEY_MAP.fedex:
      return getShipmentTrackingLabel('MARKETPLACE_RETURN');
    case RETURN_METHOD_KEY_MAP.posReturn:
      if (isRMAVisibilityChangesEnabled.value) {
        // NOTE: If KS property is coming as true then we show all 4 steps in progress bar
        return getShipmentTrackingLabel('POS_RETURN');
      }
      return getShipmentTrackingLabel('MARKETPLACE_RETURN');

    default:
      return [];
  }
});
const hazmatItems = computed(() => {
  const items = returnDetails.value.items ?? [];
  return items.filter((item) => item?.isHazmatItem);
});
const instructions = computed(() => returnInstructions[returnDropOffMethod.value]);
const isGiftCard = computed(() => [REFUND_METHOD.GIFT, REFUND_METHOD.EGIFT].includes(returnDetails.value.refundMethod));
const containsAreaRugItem = computed(() => isMcom && returnDetails.value.items?.some((item) => item.itemDetails.department?.departmentId === 911));
const locatorLinkText = computed(() => {
  const linkText = LOCATOR_LINK_TEXT.config()[returnDropOffMethod.value];
  if (containsAreaRugItem.value && returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.storeReturn) return `Eligible ${linkText.toLocaleLowerCase()}`;
  return linkText;
});
const generateProducts = computed(() =>
  transformProducts({
    page: 'return-details',
    products: returnDetails.value.items,
    isMkpOrder: isMarketplaceOrder.value,
    inlineSizeAndColor: true,
  }),
);
const showQrCode = computed(() => [RETURN_METHOD_KEY_MAP.labelFree, RETURN_METHOD_KEY_MAP.storeReturn].includes(returnDropOffMethod.value));
const hideQRCodeNumber = computed(
  () =>
    returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.storeReturn ||
    (returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.labelFree && returnDetails.value.dropOffUrl),
);
const showRefundInfo = computed(
  () =>
    returnDropOffMethod.value !== RETURN_METHOD_KEY_MAP.storeReturn &&
    returnStatusCode.value !== 303 &&
    returnDetails.value.items?.some((item) => item.returnType === 'PR'),
);
const showBankRefundText = computed(() => returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.storeReturn && !isGiftCard.value);

const isBorisReturn = computed(() => {
  const isStoreReturn = returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.posReturn;
  return isStoreReturn && isMarketplaceOrder.value;
});

const progressBarValue = computed(() => getShipmentStatus(returnStatusCode.value, isBorisReturn.value ? 'POS' : ''));

async function showTrackReturnSlideout() {
  const tagData = {
    event_name: 'return details link',
    link_name: 'ups track return',
  };
  fireTag('link', tagData);
  trackPackageStore.GET_TRACKING_DETAILS({
    trackingNumber: returnDetails.value.returnTrackingNumber,
    carrierName: returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.fedex ? RETURN_METHOD_KEY_MAP.fedex : RETURN_METHOD_KEY_MAP.ups,
    isMkpShipment: isMarketplaceOrder.value,
  });
  registerSlideout({
    id: 'return-details-slideout', // Same id is used for slideout, to prevent multiple instance issue.
    title: 'Tracking Details',
    component: markRaw(
      defineAsyncComponent(() => import(/* webpackChunkName: "tracking-details-slideout" */ '@/components/ppe/TrackingDetailsSlideout')),
    ),
  });
  toggleSlideout('return-details-slideout');
}
async function triggerLocatorSlideout(event) {
  const {
    addressLine1 = '',
    addressLine2 = '',
    addressLine3 = '',
    city = '',
    state = '',
    postalCode = '',
  } = {
    ...(returnDetails.value.contact?.address ?? {}),
  };
  const address = UserStatus.isSignedIn() ? [addressLine1, addressLine2, addressLine3, city, state, postalCode].filter((i) => i).join(', ') : '';
  if (returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.ups) {
    const tagData = {
      event_name: 'return details link',
      link_name: 'ups locations near you',
    };
    fireTag('link', tagData);
  } else if (returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.storeReturn) {
    const tagData = {
      event_name: 'return details link',
      link_name: 'store locations near you',
    };
    fireTag('link', tagData);
  }
  registerSlideout({
    id: 'return-details-slideout', // Same id is used for slideout, to prevent multiple instance issue.
    title: getTitle(returnDropOffMethod.value, containsAreaRugItem.value),
    props: {
      typeOf: returnDropOffMethod.value,
      address,
      useMspLocatorApi: true,
      newGoogleApiCall: returnDetails.value.isNewGoogleAPICallEnabled,
      locationRadius: LOCATOR_LOCATION_RADIUS,
      resultsDisplayed: LOCATOR_RESULT_LIMIT,
      showMyStoreCTA: false,
      areaRugItems: containsAreaRugItem.value,
      separateOutlets: true,
      page: 'returns',
      excludeGob: true,
      storeInPreferenceDatabaseEnabled: returnItemStore.returnItems?.storeInPreferenceDatabaseEnabled,
      newPlacesDetailsApiEnabled: returnItemStore.returnItems?.newPlacesDetailsApiEnabled,
      commonLocationServiceApiKey: envPropsStore.envProps.xMacysApiKey,
    },
    elementIdToFocus: event?.target?.id,
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "locator-slideout" */ '@nuxt-common/locator/app/components/Locator'))),
  });
  toggleSlideout('return-details-slideout');
}

async function showRefundSlideout(event) {
  const tagData = {
    event_name: 'return details link',
    link_name: 'ups refund',
  };
  fireTag('link', tagData);
  registerSlideout({
    id: 'return-details-slideout', // Same id is used for slideout, to prevent multiple instance issue with slideout
    title: 'Refund Information',
    props: {
      returnDetails,
      showStepper: true,
      isBorisReturn,
    },
    elementIdToFocus: event?.target?.id,
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "refund-instructions-slideout" */ '@/components/returns/RefundInfo'))),
  });
  toggleSlideout('return-details-slideout');
}

async function showReturnLabel() {
  const tagData = {
    event_name: 'return details link',
    link_name: 'ups get return label',
  };
  fireTag('link', tagData);
  registerModal({
    id: 'return-label',
    title: 'How to get your return label',
    width: '628px',
    component: markRaw(defineAsyncComponent(() => import('@/components/returns/GetReturnLabel'))),
  });
  toggleModal('return-label');
}

function showLocatorSlideout() {
  if (route.query.showLocatorModal === 'true' && returnStatusCode.value === 300) {
    triggerLocatorSlideout();
  }
}

function getQRcodeNumber() {
  if (returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.labelFree)
    return returnDetails.value.isRmaReturn
      ? returnDetails.value.returnTrackingNumber?.toString()
      : returnDetails.value.labelFreeReturnTrackingNumber?.toString();
  if (returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.storeReturn) {
    return returnDetails.value.pendingReturnId?.toString();
  }
  return '';
}

function getScanText() {
  if (returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.labelFree) return 'Scan in The UPS Store';
  if (returnDropOffMethod.value === RETURN_METHOD_KEY_MAP.storeReturn) return 'Scan in store';
  return '';
}

onMounted(() => {
  if (route.query.showLocatorModal) showLocatorSlideout();
});
</script>

<style lang="scss" scoped>
.full-line {
  border-bottom: $gray-3-border;

  @media screen and (max-width: 600px) {
    display: none;
  }
}

.qr-code {
  display: flex;
  justify-content: flex-end;

  @media screen and (max-width: 600px) {
    justify-content: center;
  }
}
</style>
<style lang="scss">
.print-return-label-confirmation-container {
  display: none;
}

@media print {
  #nav-header-wrapper,
  #nav-header,
  #nav-footer {
    display: none;
  }

  section .banner-content {
    display: none !important;
  }

  .print-return-label-confirmation-container {
    display: block;
    position: relative;
    overflow-y: visible;
  }

  #return-label {
    display: none;
  }
}
</style>
